export const STYLE_COLORS = {
    minimalist_clean: 'from-gray-100 to-white',
    bold_typography: 'from-gray-900 to-gray-700',
    gradient_vibrant: 'from-purple-500 to-pink-500',
    glassmorphism: 'from-blue-200/50 to-purple-200/50',
    neo_brutalism: 'from-yellow-400 to-red-400',
    photorealistic: 'from-amber-100 to-amber-50',
    flat_material: 'from-blue-400 to-blue-500',
    '3d_claymorphism': 'from-pink-200 to-purple-200',
    retro_futurism: 'from-purple-600 to-blue-600',
    cyberpunk_neon: 'from-blue-600 to-fuchsia-600',
    watercolor_organic: 'from-green-200 to-amber-100',
    pop_art: 'from-red-500 to-yellow-400',
    vintage_heritage: 'from-amber-200 to-amber-100',
    isometric_3d: 'from-teal-400 to-cyan-400',
    mixed_media: 'from-orange-300 to-rose-300',
    editorial_cinematic: 'from-gray-700 to-gray-900',
    bento_grid: 'from-slate-200 to-slate-100',
    lo_fi_diy: 'from-yellow-100 to-orange-100',
    solarpunk: 'from-green-400 to-emerald-300',
    vaporwave: 'from-pink-300 to-violet-300',
    art_deco: 'from-yellow-600 to-amber-700',
    grunge_distressed: 'from-stone-500 to-stone-700',
    duotone: 'from-indigo-500 to-rose-500',
    paper_cut: 'from-sky-200 to-sky-100',
    neon_noir: 'from-gray-900 to-purple-900',
    photo_overlay: 'from-slate-700 to-slate-900',
    magazine_cover: 'from-red-600 to-red-800',
    split_screen: 'from-teal-400 to-cyan-500',
    dark_luxury: 'from-gray-800 to-gray-950',
    text_container: 'from-violet-400 to-fuchsia-400',
    bold_hook_3d: 'from-orange-300 to-rose-200',
    bold_statement: 'from-red-600 to-red-600',
};

export const marketingStyles = [
    // Tier 1 — Most Popular
    {
        key: 'minimalist_clean',
        name: 'Minimalist Clean',
        tier: 1,
        description: 'Clean white space with thin typography and single accent color',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/minimalist_clean.png',
        promptModifier: 'Clean minimalist full-frame design. Generous white space, thin sans-serif typography as the primary visual element, single accent color, pure uncluttered background extending to all edges. Balanced composition, elegant and modern.',
    },
    {
        key: 'bold_typography',
        name: 'Bold Typography',
        tier: 1,
        description: 'Oversized bold text as the focal point with dramatic hierarchy',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/bold_typography.png',
        promptModifier: 'Full-frame typographic design with bold oversized lettering dominating the composition. Dramatic type hierarchy, strong contrast between text and background. The typography IS the focal point — minimal imagery, maximum text impact.',
    },
    {
        key: 'gradient_vibrant',
        name: 'Gradient / Vibrant',
        tier: 1,
        description: 'Vivid gradient backgrounds with smooth color transitions',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/gradient_vibrant.png',
        promptModifier: 'Vivid luminous color gradient sweeping across the entire canvas as the background, smooth transitions between vibrant saturated colors from edge to edge. Content placed directly on the gradient with no panels, cards, or frames between them. Contemporary, bold, and eye-catching.',
    },
    {
        key: 'bold_hook_3d',
        name: 'Bold Hook 3D',
        tier: 1,
        description: 'Punchy 3D hook on pastel cover, clean white content slides with glossy accents',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/bold_hook_3d.png',
        promptModifier: 'Bold attention-grabbing 3D hook style with TWO DISTINCT visual modes — you MUST use background_override per image to set each image\'s background independently. COVER IMAGE (position 1): Use background_override to set a solid pastel color filling the entire canvas — choose a pastel tint derived from the brand palette or topic theme. Massive bold white sans-serif headline — provocative, punchy, scroll-stopping hook phrase. A prominent glossy 3D rendered character or object (stylized figure, mascot, playful 3D creature, or thematic 3D icon related to the topic) occupying roughly 40 percent of the frame, rendered with smooth glossy surfaces, soft studio lighting, and subtle reflections. Small scattered emoji decorations (2-3 relevant emojis) floating as playful accents. Optional small subtle credit or subtitle text near the top edge. High energy, bold, unapologetic. CONTENT IMAGES (position 2 and beyond): Use background_override to set a clean white or very light off-white background filling the entire canvas. Dark near-black text as the main body, with one or two key phrases or words highlighted in the brand accent color. A prominent glossy 3D rendered shape or icon (rounded app-icon style cube, glossy gem, smooth abstract blob, or polished 3D object) placed in one corner as a strong visual anchor, roughly 20-25 percent of the frame. Generous whitespace, spacious minimal layout. Each content image frames a problem-vs-solution contrast: the pain point at the top followed by the quick solution at the bottom. A small relevant emoji next to key phrases adds personality. ACROSS ALL IMAGES: Bold heavy-weight sans-serif typography with large impactful headlines. Every 3D element must look glossy, smooth, and professionally rendered with soft studio lighting and subtle reflections — never matte clay, never flat. Clean, modern, social-media native aesthetic. No clutter, no borders, no device frames, no mockups.',
    },
    {
        key: 'bold_statement',
        name: 'Bold Statement',
        tier: 1,
        description: 'Solid color background with mixed-font manifesto text and generous whitespace',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/bold_statement.png',
        promptModifier: 'Manifesto-style typographic poster on a completely flat solid single-color background — one uniform color fills the entire canvas with absolutely no gradient, no texture, no pattern, no grain, and no imagery. The text is the entire composition. Use two contrasting typefaces within the same text block: the majority of words in a heavy bold sans-serif, with one or two key emphasis words or phrases rendered in a flowing cursive handwriting script to create dramatic typographic contrast. Text is generously inset from all four edges with at least 15-20% margin on every side — no text approaches the canvas boundary. Zero decorative elements: no icons, no illustrations, no shapes, no lines, no borders, no shadows, no logos. Only text on a flat color field. The feeling is a confident manifesto — direct, high-impact, stripped-down to pure message.',
    },
    {
        key: 'glassmorphism',
        name: 'Glassmorphism',
        tier: 1,
        description: 'Frosted glass panels with translucent blur effects',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/glassmorphism.png',
        promptModifier: 'Frosted translucent glass aesthetic layered across the full composition. Soft blurred depth with subtle light refraction, gentle luminous borders on semi-transparent elements, ethereal bokeh glow behind. The entire scene has the frosted-glass quality throughout — not a glass card placed on a separate background. Airy, modern, layered.',
    },
    {
        key: 'neo_brutalism',
        name: 'Neo-Brutalism',
        tier: 1,
        description: 'Raw geometric shapes with thick outlines and bright fills',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/neo_brutalism.png',
        promptModifier: 'Raw geometric shapes with thick black outlines and bright saturated solid fills covering the full canvas. Exposed grid, anti-design aesthetic, bold and unapologetic. High-energy composition with intentionally rough, punchy color blocks.',
    },
    {
        key: 'photorealistic',
        name: 'Photorealistic Product',
        tier: 1,
        description: 'Studio-quality product photography with clean backgrounds',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/photorealistic.png',
        promptModifier: 'Photorealistic studio photography with professional three-point lighting, clean background, tack-sharp focus. Shot at 85mm f/2.8, subtle contact shadows, commercial advertising quality. Crisp detail, product-catalog grade.',
    },
    {
        key: 'flat_material',
        name: 'Flat / Material',
        tier: 1,
        description: 'Solid color blocks with geometric shapes and clean edges',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/flat_material.png',
        promptModifier: 'Full-frame flat design using bold solid color blocks and clean geometric shapes extending to every edge of the canvas. Material design language with subtle elevation shadows. The colored shapes and blocks form the entire background — no device mockups, no browser frames. Simple, modern, direct.',
    },
    {
        key: '3d_claymorphism',
        name: '3D / Claymorphism',
        tier: 1,
        description: 'Soft 3D clay-like elements with rounded pastel shapes',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/3d_claymorphism.png',
        promptModifier: 'Soft 3D claymation-style elements with rounded pastel shapes, playful depth, and smooth matte surfaces. Gentle ambient occlusion lighting, friendly and approachable scene. Soft shadows on a clean pastel background.',
    },

    // Tier 2 — Trending
    {
        key: 'retro_futurism',
        name: 'Retro Futurism',
        tier: 2,
        description: 'Synthwave colors with chrome elements and vintage sci-fi vibes',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/retro_futurism.png',
        promptModifier: 'Immersive retro-futuristic scene with synthwave palette — electric pinks, purples, and deep blues filling the frame. Chrome metallic elements, neon grid lines extending to the horizon, laser accents, vintage sci-fi atmosphere. 80s future vision covering the entire canvas — not a poster within the image.',
    },
    {
        key: 'cyberpunk_neon',
        name: 'Cyberpunk / Neon',
        tier: 2,
        description: 'Dark backgrounds with electric neon glow effects',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/cyberpunk_neon.png',
        promptModifier: 'Dark immersive cyberpunk composition with electric neon glow in blue and magenta illuminating the scene from within. Urban tech atmosphere, high-contrast lighting with neon reflections on dark surfaces. The dark background with neon illumination fills the entire canvas edge-to-edge. Futuristic and electric.',
    },
    {
        key: 'watercolor_organic',
        name: 'Watercolor / Organic',
        tier: 2,
        description: 'Soft watercolor textures with flowing organic shapes',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/watercolor_organic.png',
        promptModifier: 'Soft watercolor textures with organic flowing shapes and natural earth tones across the full canvas. Hand-painted aesthetic with gentle color bleeding, botanical accents, warm ambient light. Artistic, serene, and delicate.',
    },
    {
        key: 'pop_art',
        name: 'Pop Art',
        tier: 2,
        description: 'Bold halftone dots with primary colors and comic energy',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/pop_art.png',
        promptModifier: 'Bold pop art composition with halftone dot patterns, bright primary colors, and comic-book energy filling the frame. Warhol-inspired high saturation, thick outlines, retro commercial illustration style. Vibrant, loud, and graphic.',
    },
    {
        key: 'vintage_heritage',
        name: 'Vintage / Heritage',
        tier: 2,
        description: 'Aged paper textures with classic serif typography',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/vintage_heritage.png',
        promptModifier: 'Vintage aesthetic with aged paper texture, classic serif typography, and muted warm tones throughout. Heritage branding feel with subtle grain and sepia undertones. Timeless, nostalgic, old-world craftsmanship atmosphere.',
    },
    {
        key: 'isometric_3d',
        name: 'Isometric 3D',
        tier: 2,
        description: 'Geometric precision with vibrant flat-colored 3D illustrations',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/isometric_3d.png',
        promptModifier: 'Isometric 3D illustration with geometric precision and vibrant flat colors. Technical yet playful perspective showing elements from above at a 30-degree angle. Clean vector-like rendering with soft cast shadows.',
    },
    {
        key: 'mixed_media',
        name: 'Mixed Media Collage',
        tier: 2,
        description: 'Cut-and-paste collage with mixed textures and editorial feel',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/mixed_media.png',
        promptModifier: 'Cut-and-paste collage aesthetic with mixed textures, layered torn-paper elements, and editorial feel across the canvas. Magazine clippings meets digital design, creative and eclectic composition with visual depth.',
    },
    {
        key: 'editorial_cinematic',
        name: 'Editorial Cinematic',
        tier: 2,
        description: 'Cinematic lighting with dramatic shadows and magazine quality',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/editorial_cinematic.png',
        promptModifier: 'Cinematic dramatic lighting with deep shadows and selective highlights. Editorial photography style with magazine-quality composition. Moody color grading, sophisticated atmosphere. Shot as if for a high-end publication.',
    },
    {
        key: 'bento_grid',
        name: 'Bento Grid',
        tier: 2,
        description: 'Bento box layout with asymmetric compartments',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/bento_grid.png',
        promptModifier: 'Bento box grid layout filling the canvas with asymmetric compartments separated by clean thin borders. Each compartment contains its own visual element. Structured modern design with clear visual hierarchy, organized yet dynamic.',
    },

    // Tier 3 — Emerging
    {
        key: 'lo_fi_diy',
        name: 'Lo-Fi / DIY',
        tier: 3,
        description: 'Handmade aesthetic with notebook sketches and tape strips',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/lo_fi_diy.png',
        promptModifier: 'Lo-fi handmade aesthetic with notebook-style sketches, tape strips, doodles, and authentic imperfections. Lined paper or kraft paper fills the background, hand-drawn elements, raw and personal creative expression.',
    },
    {
        key: 'solarpunk',
        name: 'Solarpunk',
        tier: 3,
        description: 'Lush greenery with technology and hopeful futurism',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/solarpunk.png',
        promptModifier: 'Solarpunk scene with lush greenery integrated into futuristic technology, organic architecture, solar panels. Bright natural sunlight flooding the composition, sustainable-living atmosphere. Vibrant greens and warm golds.',
    },
    {
        key: 'vaporwave',
        name: 'Vaporwave',
        tier: 3,
        description: 'Pastel gradients with retro computing and 80s nostalgia',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/vaporwave.png',
        promptModifier: 'Vaporwave aesthetic with pastel pink and cyan gradients across the full canvas. Greek statue fragments, retro computing elements, glitch effects, and geometric wireframes. 80s-90s nostalgia, dreamy and surreal digital landscape extending edge-to-edge.',
    },
    {
        key: 'art_deco',
        name: 'Art Deco',
        tier: 3,
        description: 'Geometric patterns with gold accents and luxurious symmetry',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/art_deco.png',
        promptModifier: 'Art deco geometric patterns with metallic gold accents on deep jewel-tone backgrounds covering the full canvas. Luxurious symmetry, gatsby-era elegance, radiating sunburst motifs. Opulent, refined, and ornate.',
    },
    {
        key: 'grunge_distressed',
        name: 'Grunge / Distressed',
        tier: 3,
        description: 'Grunge textures with scratched surfaces and urban edge',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/grunge_distressed.png',
        promptModifier: 'Grunge textures with distressed overlays, scratched and worn surfaces filling the entire frame. Raw urban edge, concrete and rust tones, rebellious energy. Weathered industrial aesthetic with tactile texture throughout.',
    },
    {
        key: 'duotone',
        name: 'Duotone',
        tier: 3,
        description: 'Two-color gradient overlay with high contrast editorial look',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/duotone.png',
        promptModifier: 'Dramatic two-color duotone treatment applied across the entire image from edge to edge. Two bold contrasting colors create a striking high-contrast composition covering the full canvas. The duotone coloring IS the image — not a filter swatch or preview. Modern editorial, graphic and bold.',
    },
    {
        key: 'paper_cut',
        name: 'Paper Cut',
        tier: 3,
        description: 'Layered paper cut effect with dimensional shadows',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/paper_cut.png',
        promptModifier: 'Layered paper-cut effect with dimensional shadows between stacked colorful paper shapes. Clean cut edges, crafted depth from multiple layers filling the entire canvas. Intricate, tactile, and handcrafted.',
    },
    {
        key: 'neon_noir',
        name: 'Neon Noir',
        tier: 3,
        description: 'Film noir atmosphere with neon signage and moody nightscape',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/neon_noir.png',
        promptModifier: 'Film noir atmosphere with neon signage casting colored light pools on rain-slicked surfaces. Deep shadows with selective neon illumination in pink, blue, and purple. Moody urban nightscape, mysterious and cinematic.',
    },

    // Tier 1 — Classic Post Formats
    {
        key: 'photo_overlay',
        name: 'Lifestyle Photo Overlay',
        tier: 1,
        description: 'Full-bleed photo with gradient text zone',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/photo_overlay.png',
        promptModifier: 'Full-bleed atmospheric or lifestyle photograph covering the entire canvas edge-to-edge. A smooth gradient fades from fully transparent at the top to deep charcoal or rich dark at the lower third, creating a legible text-safe zone. Bold sans-serif headline sits cleanly in the gradient zone, crisp white text on the darkened area. Small brand name or handle at the top. The photo IS the background — cinematic, authentic, editorial.',
    },
    {
        key: 'magazine_cover',
        name: 'Magazine Cover',
        tier: 1,
        description: 'Hero photo with editorial masthead bands',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/magazine_cover.png',
        promptModifier: 'Hero image fills the entire canvas as background. Bold condensed display headline in a solid-colored horizontal masthead band at the top, high-contrast against the photo. A second horizontal bar at the bottom holds supporting text and the brand label. Classic editorial magazine proportions — authoritative typographic hierarchy over full-bleed photography, bold and punchy.',
    },
    {
        key: 'split_screen',
        name: 'Split Screen Duo',
        tier: 1,
        description: 'Half photo, half bold color panel with headline',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/split_screen.png',
        promptModifier: 'Canvas divided into two equal vertical halves with a crisp hard edge. One half is a lifestyle or product photograph. The other half is a bold solid brand-color panel with the headline in a large contrasting typeface and supporting copy below, plus a simple geometric accent element. Clean, structured, high contrast between organic photo and graphic color panel.',
    },
    {
        key: 'dark_luxury',
        name: 'Dark Luxury',
        tier: 1,
        description: 'Deep dark background with cream or gold typography',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/dark_luxury.png',
        promptModifier: 'Deep charcoal, near-black, or rich navy background filling the entire canvas edge-to-edge. Headline and text in warm cream, ivory, or metallic gold letterforms — refined, confident, and elegant. Subtle background texture: fine grain, barely-visible geometric motif, or soft material texture. Sparse minimal composition with intentional negative space and a single elegant accent detail. Understated luxury.',
    },
    {
        key: 'text_container',
        name: 'Text Container / Card',
        tier: 1,
        description: 'Geometric text container on photo or gradient',
        previewUrl: 'https://followr.blob.core.windows.net/develop/creative-studio/style-previews/text_container.png',
        promptModifier: 'Vibrant lifestyle photo or rich color gradient fills the entire background edge-to-edge. A clean rounded rectangle or card — semi-transparent frosted glass or bold solid brand color — is anchored in the center or lower section, containing the headline and body text. The container shape clearly separates text from the organic background. Modern overlay post layout with strong contrast between the geometric card and the atmospheric background.',
    },
];
